'use client';

import React from 'react';
import type { CaptionProps } from 'react-day-picker';

import { Calendar as BaseCalendar } from '@ui/components/ui/calendar';

import { CalendarCaption } from './CalendarCaption';

// Default empty array for datesWithEvents prop
const DEFAULT_DATES_WITH_EVENTS: Date[] = [];

// Use a simpler interface that allows any DayPicker props plus our custom props

export interface CalendarProps extends Record<string, unknown> {
  className?: string;
  classNames?: Record<string, string>;
  circularDays?: boolean;
  datesWithEvents?: Date[];
  separateYearMonthNav?: boolean;
  shortenMonthNames?: boolean;
  yearRange?: { fromYear: number; toYear: number };
  locale?: string;
  // Common DayPicker props
  mode?: 'single' | 'multiple' | 'range' | 'default';
  modifiers?: Record<string, unknown>;
  modifiersClassNames?: Record<string, string>;
}

function Calendar({
  className,
  classNames,
  circularDays = false,
  datesWithEvents = DEFAULT_DATES_WITH_EVENTS,
  separateYearMonthNav = false,
  shortenMonthNames = false,
  yearRange,
  locale = 'en',
  ...props
}: CalendarProps) {
  // Format date as local YYYY-MM-DD string (avoids UTC conversion that can shift days)
  const formatDateLocal = React.useCallback((date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Create a set of date strings for fast lookup
  const eventDateStrings = React.useMemo(() => {
    return new Set(datesWithEvents.map((date) => formatDateLocal(date)));
  }, [datesWithEvents, formatDateLocal]);

  // Custom matcher for dates without events
  const datesWithoutEvents = React.useCallback(
    (date: Date) => {
      const dateString = formatDateLocal(date);
      return !eventDateStrings.has(dateString);
    },
    [eventDateStrings, formatDateLocal]
  );

  // Enhanced class names with circular days and event highlighting support
  const enhancedClassNames = React.useMemo(() => {
    const baseClassNames = classNames || {};

    return {
      ...baseClassNames,
      head_cell: `text-muted-foreground rounded-md w-8 font-normal text-[0.8rem] ${circularDays ? 'mx-1' : ''}`,
      cell: `relative text-center text-sm focus-within:relative focus-within:z-20 ${circularDays ? 'p-1' : 'p-0'} ${
        !circularDays ? '[&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-range-end)]:rounded-r-md' : ''
      } ${
        props.mode === 'range'
          ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
          : !circularDays
            ? '[&:has([aria-selected])]:rounded-md'
            : ''
      }`,
      day: `bg-transparent cursor-pointer hover:bg-accent hover:text-accent-foreground size-8 font-normal aria-selected:opacity-100 ${
        circularDays ? 'p-0 rounded-full flex items-center justify-center' : 'p-0 rounded-md'
      }`,
      day_selected: circularDays
        ? 'border-2 border-primary text-primary bg-transparent hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary rounded-full'
        : 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
      day_today: circularDays
        ? 'border border-accent text-accent-foreground bg-transparent rounded-full'
        : 'bg-accent text-accent-foreground'
    };
  }, [classNames, circularDays, props.mode]);

  // Set up modifiers for dates without events if we have event data
  const modifiers = React.useMemo(() => {
    const baseModifiers = props.modifiers || {};

    if (datesWithEvents.length > 0) {
      return {
        ...baseModifiers,
        noEvents: datesWithoutEvents
      };
    }

    return baseModifiers;
  }, [props.modifiers, datesWithEvents.length, datesWithoutEvents]);

  // Set up modifier class names for dates without events
  const modifiersClassNames = React.useMemo(() => {
    const baseModifiersClassNames = props.modifiersClassNames || {};

    if (datesWithEvents.length > 0) {
      return {
        ...baseModifiersClassNames,
        noEvents: 'text-gray-400 opacity-60'
      };
    }

    return baseModifiersClassNames;
  }, [props.modifiersClassNames, datesWithEvents.length]);

  // Create a custom Caption component wrapper to pass props
  const CustomCaption = React.useMemo(
    () => (captionProps: CaptionProps) => (
      <CalendarCaption {...captionProps} shortenMonthNames={shortenMonthNames} locale={locale} />
    ),
    [shortenMonthNames, locale]
  );

  return (
    <BaseCalendar
      {...props}
      fromYear={separateYearMonthNav ? yearRange?.fromYear : undefined}
      toYear={separateYearMonthNav ? yearRange?.toYear : undefined}
      className={className}
      classNames={enhancedClassNames}
      modifiers={modifiers}
      modifiersClassNames={modifiersClassNames}
      locale={locale}
      components={
        separateYearMonthNav
          ? {
              Caption: CustomCaption
            }
          : undefined
      }
    />
  );
}

export { Calendar };
